import axiosInstance from '../../services/axiosInstance';
import {
  IInhouseEntry,
  IInhouseEntriesResponse,
  IInhouseCategory,
  IInhouseStatsResponse,
  IInhouseMedia,
  IInhouseReactionSummary,
  IInhouseEntryFormData,
} from './types';

// =============================================
// Categories
// =============================================

export async function fetchCategories(): Promise<IInhouseCategory[]> {
  const { data } = await axiosInstance.get<IInhouseCategory[]>('/api/inhouse/categories');
  return data;
}

export async function createCategory(cat: { id: string; label: string; color: string }): Promise<IInhouseCategory> {
  const { data } = await axiosInstance.post<IInhouseCategory>('/api/inhouse/categories', cat);
  return data;
}

export async function updateCategory(id: string, updates: { label?: string; color?: string; order?: number }): Promise<IInhouseCategory> {
  const { data } = await axiosInstance.put<IInhouseCategory>(`/api/inhouse/categories/${id}`, updates);
  return data;
}

export async function deleteCategory(id: string): Promise<void> {
  await axiosInstance.delete(`/api/inhouse/categories/${id}`);
}

export async function reorderCategories(order: { id: string; order: number }[]): Promise<IInhouseCategory[]> {
  const { data } = await axiosInstance.put<IInhouseCategory[]>('/api/inhouse/categories/reorder', { order });
  return data;
}

// =============================================
// Current week
// =============================================

export async function fetchCurrentWeek(): Promise<{ weekNumber: number; year: number }> {
  const { data } = await axiosInstance.get<{ weekNumber: number; year: number }>('/api/inhouse/current-week');
  return data;
}

// =============================================
// Entries CRUD
// =============================================

export interface FetchEntriesParams {
  category?: string;
  search?: string;
  year?: number;
  weekNumber?: number;
  limit?: number;
  offset?: number;
}

export async function fetchEntries(params?: FetchEntriesParams): Promise<IInhouseEntriesResponse> {
  const { data } = await axiosInstance.get<IInhouseEntriesResponse>('/api/inhouse/entries', { params });
  return data;
}

export async function fetchEntry(id: string): Promise<IInhouseEntry> {
  const { data } = await axiosInstance.get<IInhouseEntry>(`/api/inhouse/entries/${id}`);
  return data;
}

export async function createEntry(entryData: IInhouseEntryFormData): Promise<IInhouseEntry> {
  const { data } = await axiosInstance.post<IInhouseEntry>('/api/inhouse/entries', entryData);
  return data;
}

export async function updateEntry(id: string, entryData: Partial<IInhouseEntryFormData>): Promise<IInhouseEntry> {
  const { data } = await axiosInstance.put<IInhouseEntry>(`/api/inhouse/entries/${id}`, entryData);
  return data;
}

export async function deleteEntry(id: string): Promise<void> {
  await axiosInstance.delete(`/api/inhouse/entries/${id}`);
}

// =============================================
// Reactions
// =============================================

export async function toggleReaction(entryId: string, type: string): Promise<{ reactions: any[]; summary: IInhouseReactionSummary }> {
  const { data } = await axiosInstance.post(`/api/inhouse/entries/${entryId}/reactions`, { type });
  return data;
}

// =============================================
// Media upload
// =============================================

export async function uploadMedia(files: File[], onProgress?: (percent: number) => void): Promise<IInhouseMedia[]> {
  const formData = new FormData();
  files.forEach(file => formData.append('media', file));

  const { data } = await axiosInstance.post<{ media: IInhouseMedia[] }>('/api/inhouse/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percent);
      }
    },
  });
  return data.media;
}

export async function searchAthenaMedia(query: string): Promise<IInhouseMedia[]> {
  const { data } = await axiosInstance.get<{ media: IInhouseMedia[] }>('/api/inhouse/athena/search', {
    params: { q: query },
  });
  return data.media;
}

// =============================================
// Custom thumbnail upload
// =============================================

export async function uploadThumbnail(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('thumbnail', file);
  const { data } = await axiosInstance.post<{ thumbnailUrl: string }>('/api/inhouse/upload-thumbnail', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.thumbnailUrl;
}

// =============================================
// Stats
// =============================================

export async function fetchStats(year?: number, month?: number): Promise<IInhouseStatsResponse> {
  const params: Record<string, number> = {};
  if (year) params.year = year;
  if (month) params.month = month;
  const { data } = await axiosInstance.get<IInhouseStatsResponse>('/api/inhouse/stats', { params });
  return data;
}
